import { registerWebComponent } from '@sdx/b2o-navigation-module';
import { useEffect, useRef, useCallback, Dispatch, SetStateAction } from 'react';
import { useUser } from '../../core/contexts/UserContext';
import { useAuth } from '../../core/contexts/AuthContext';
import { useLocation, useNavigate } from 'react-router';
import useNavigationStore from '../../core/stores/navigationStore';
import useExternalLinksStore from '../../core/stores/externalLinksStore';
import { useModuleFeedback } from '../../core/hooks/useModuleFeedback';
import { useTranslation } from 'react-i18next';
import { useApp } from '../../core/contexts/AppContext';
import { useIsDesktop } from '../../core/hooks/useMediaQuery';
import { isChatbotHiddenOnPage } from '../../core/constants/chatbotRoutes';
import { shouldShowAccessibilityLabel } from './utils/shouldShowAccessibilityLabel';

interface NavigationModuleProps {
  isFullScreen?: boolean;
  isOpen?: boolean;
  setIsMenuOpen?: Dispatch<SetStateAction<boolean>>;
  onClose?: () => void;
  bottomOffset?: string;
  onSiteSelectorOpen?: (isOpen: boolean) => void;
}

const NavigationModule = ({
  isFullScreen = false,
  isOpen = false,
  setIsMenuOpen,
  onClose,
  bottomOffset = '0px',
  onSiteSelectorOpen,
}: NavigationModuleProps) => {
  const location = useLocation();
  const { setIsChatbotOpen, handleOpenAliciaChatbot } = useApp();
  const { siteCode, setSiteCode } = useUser();
  const { accessToken } = useAuth();
  const { breadcrumbs, setBreadcrumbPath, blockNavigation, hasPendingChanges, confirmNavigation } =
    useNavigationStore();
  const {
    saveStatus,
    discardRequested: isDiscardRequested,
    setPendingChanges,
    completeSave,
    clearDiscardRequest,
  } = useExternalLinksStore();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('common');
  const isDesktop = useIsDesktop();

  const moduleRef = useRef<HTMLElement>(null);

  useEffect(() => {
    registerWebComponent('b2o-navigation-module');
  }, []);

  useModuleFeedback(moduleRef);

  const handleMobileClose = useCallback(() => {
    if (onClose) {
      onClose();
    } else if (setIsMenuOpen) {
      setIsMenuOpen(false);
    }
  }, [onClose, setIsMenuOpen]);

  const handleNavigateHomescreen = useCallback(() => {
    handleMobileClose();

    if (hasPendingChanges) {
      blockNavigation('/home');
    } else {
      navigate('/home');
    }
  }, [
    hasPendingChanges,
    blockNavigation,
    navigate,
    breadcrumbs,
    setBreadcrumbPath,
    handleMobileClose,
  ]);

  const handleNavigateGes = useCallback(() => {
    handleMobileClose();

    if (hasPendingChanges) {
      blockNavigation('/ges');
    } else {
      navigate('/ges');
      setBreadcrumbPath([
        { key: 'homepage', label: t('common:navigation.homePage'), path: '/home' },
        { key: 'ges', label: t('common:ges.title'), path: '/ges' },
      ]);
      setIsChatbotOpen(false);
    }
  }, [
    hasPendingChanges,
    blockNavigation,
    navigate,
    breadcrumbs,
    setBreadcrumbPath,
    handleMobileClose,
    setIsChatbotOpen,
  ]);

  const handleNavigateConfigurations = useCallback(() => {
    handleMobileClose();
    if (hasPendingChanges) {
      blockNavigation('/settings');
    } else {
      navigate('/settings');
    }
  }, [handleMobileClose, navigate, hasPendingChanges, blockNavigation]);

  const handleNavigateMyTasks = useCallback(() => {
    handleMobileClose();
    if (hasPendingChanges) {
      blockNavigation('/my-tasks');
    } else {
      navigate('/my-tasks');
    }
  }, [handleMobileClose, navigate, hasPendingChanges, blockNavigation]);

  const handleNavigateAccessibility = useCallback(() => {
    handleMobileClose();
    if (hasPendingChanges) {
      blockNavigation('/accessibility');
    } else {
      navigate('/accessibility');
    }
  }, [handleMobileClose, navigate, hasPendingChanges, blockNavigation]);

  const handleSiteSelectorOpen = useCallback(
    (event: Event) => {
      const isOpen = (event as CustomEvent<boolean>).detail;
      onSiteSelectorOpen?.(isOpen);
    },
    [onSiteSelectorOpen],
  );

  const handleSelectSite = useCallback(
    (event: Event) => {
      const { cod } = (event as CustomEvent<{ cod: string; segment: string }>).detail ?? {};
      if (cod) setSiteCode(cod);
    },
    [setSiteCode],
  );

  const handleMenuClick = useCallback(
    (event: Event) => {
      const customEvent = event as CustomEvent<{ name: string; url: string }>;
      if (customEvent.detail == null) return;
      const { name, url } = customEvent.detail;
      if ((name == null || name.length === 0) && (url == null || url.length === 0)) return;

      if (name === 'Alicia') {
        if (isChatbotHiddenOnPage(location.pathname)) return;
        if (isDesktop) {
          setIsChatbotOpen(true);
        } else {
          handleOpenAliciaChatbot();
        }
        return;
      }

      if (url != null && url.length > 0) {
        if (hasPendingChanges) {
          blockNavigation(url);
        } else {
          navigate(url);
          if (!isDesktop) {
            handleMobileClose();
          }
        }
      }
    },
    [
      setIsChatbotOpen,
      isDesktop,
      location.pathname,
      navigate,
      handleMobileClose,
      handleOpenAliciaChatbot,
      hasPendingChanges,
      blockNavigation,
    ],
  );

  const handlePendingChanges = useCallback(
    (event: Event) => {
      const { isDirty } = (event as CustomEvent<{ isDirty: boolean }>).detail ?? {};
      setPendingChanges(isDirty === true);
    },
    [setPendingChanges],
  );

  const handleSaved = useCallback(
    (event: Event) => {
      const { isSuccess } = (event as CustomEvent<{ isSuccess: boolean }>).detail ?? {};
      completeSave(isSuccess === true);
      if (isSuccess === true) {
        confirmNavigation(navigate);
      }
    },
    [completeSave, confirmNavigation, navigate],
  );

  const handleDiscarded = useCallback(() => {
    clearDiscardRequest();
  }, [clearDiscardRequest]);

  useEffect(() => {
    const element = moduleRef.current;

    if (element) {
      element.addEventListener('navigatehomescreen', handleNavigateHomescreen);
      element.addEventListener('navigateges', handleNavigateGes);
      element.addEventListener('navigatemytasks', handleNavigateMyTasks);
      element.addEventListener('navigateconfigurations', handleNavigateConfigurations);
      element.addEventListener('navigateaccessibility', handleNavigateAccessibility);
      element.addEventListener('close', handleMobileClose);
      element.addEventListener('menuclick', handleMenuClick);
      element.addEventListener('siteselectoropen', handleSiteSelectorOpen);
      element.addEventListener('selectsite', handleSelectSite);
      element.addEventListener('pendingchanges', handlePendingChanges);
      element.addEventListener('saved', handleSaved);
      element.addEventListener('discarded', handleDiscarded);
    }

    return (): void => {
      if (element) {
        element.removeEventListener('navigatehomescreen', handleNavigateHomescreen);
        element.removeEventListener('navigateges', handleNavigateGes);
        element.removeEventListener('navigatemytasks', handleNavigateMyTasks);
        element.removeEventListener('navigateconfigurations', handleNavigateConfigurations);
        element.removeEventListener('navigateaccessibility', handleNavigateAccessibility);
        element.removeEventListener('close', handleMobileClose);
        element.removeEventListener('menuclick', handleMenuClick);
        element.removeEventListener('siteselectoropen', handleSiteSelectorOpen);
        element.removeEventListener('selectsite', handleSelectSite);
        element.removeEventListener('pendingchanges', handlePendingChanges);
        element.removeEventListener('saved', handleSaved);
        element.removeEventListener('discarded', handleDiscarded);
      }
    };
  }, [
    handleNavigateHomescreen,
    handleNavigateGes,
    handleNavigateMyTasks,
    handleNavigateConfigurations,
    handleNavigateAccessibility,
    handleMobileClose,
    handleMenuClick,
    handleSiteSelectorOpen,
    handleSelectSite,
    handlePendingChanges,
    handleSaved,
    handleDiscarded,
  ]);

  return (
    <b2o-navigation-module
      ref={moduleRef}
      basePath='/'
      env={String(import.meta.env.VITE_APP_ENVIRONMENT)}
      language={i18n.language}
      site-code={siteCode}
      show-accessibility-label={shouldShowAccessibilityLabel(siteCode)}
      access-token={accessToken ?? ''}
      full-screen={isFullScreen}
      open={isOpen}
      bottom-offset={bottomOffset}
      active-path={location.pathname}
      is-saving={saveStatus === 'saving'}
      is-discarding={isDiscardRequested}
    />
  );
};

export default NavigationModule;
